/**
 * Knowledge-graph engine: given a source content item, computes related
 * items across every content type (essays, concepts, books, talks, reports,
 * podcast episodes) by scoring keyword / concept / category overlap.
 *
 * All matching is pure and runs in-memory — no DB round-trips beyond the
 * primary content queries the caller already makes.
 */
import type { WpArticle } from "@/lib/posts.types";
import type { ReportRow } from "@/lib/reports.functions";
import { findGlossaryConcepts, type GlossaryConcept } from "@/lib/glossary-autolink";
import { KG_BOOKS, type KGBook } from "@/lib/kg-books";
import { talks as ALL_TALKS, type Talk } from "@/lib/talks";

export type KGLang = "tr" | "en";

/* ------------------------------------------------------------------ */
/*  Text utilities                                                     */
/* ------------------------------------------------------------------ */

function foldTr(s: string): string {
  return s
    .toLowerCase()
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/â/g, "a")
    .replace(/î/g, "i")
    .replace(/û/g, "u");
}

function tokenize(s: string): Set<string> {
  const folded = foldTr(s ?? "");
  const tokens = folded
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length >= 3);
  return new Set(tokens);
}

function overlapCount(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const t of a) if (b.has(t)) n++;
  return n;
}

function stripTags(html: string): string {
  return (html ?? "").replace(/<[^>]+>/g, " ");
}

/* ------------------------------------------------------------------ */
/*  Source profile — a "fingerprint" for the item we're finding        */
/*  related content for.                                               */
/* ------------------------------------------------------------------ */

export type KGSourceProfile = {
  slug: string;
  title: string;
  category: string;
  concepts: GlossaryConcept[];
  /** Combined keyword bag: category + title + concept labels + tags. */
  tokens: Set<string>;
};

export function buildEssayProfile(
  post: WpArticle & { content?: string },
  lang: KGLang,
): KGSourceProfile {
  const html = post.content ?? "";
  const concepts = html ? findGlossaryConcepts(html, lang) : [];
  const bag = [
    post.title ?? "",
    post.category ?? "",
    post.excerpt ?? "",
    ...concepts.map((c) => c.label),
    ...((post as unknown as { tags?: string[] }).tags ?? []),
  ].join(" ");
  return {
    slug: post.slug,
    title: post.title ?? "",
    category: post.category ?? "",
    concepts,
    tokens: tokenize(bag),
  };
}

/* ------------------------------------------------------------------ */
/*  Related essays                                                     */
/* ------------------------------------------------------------------ */

export type RelatedEssay = WpArticle & { _score: number };

export function findRelatedEssays(
  source: KGSourceProfile,
  all: WpArticle[],
  limit = 4,
): WpArticle[] {
  const scored: RelatedEssay[] = [];
  for (const p of all) {
    if (p.slug === source.slug) continue;
    const bag = [
      p.title ?? "",
      p.category ?? "",
      p.excerpt ?? "",
      ...((p as unknown as { tags?: string[] }).tags ?? []),
    ].join(" ");
    const tokens = tokenize(bag);
    let score = overlapCount(source.tokens, tokens);
    if (p.category && source.category && p.category === source.category) score += 3;
    // concept label match against target title/excerpt/tags (content is not
    // available on the list-shape WpArticle).
    if (source.concepts.length) {
      const targetText = foldTr([p.title, p.excerpt].join(" "));
      for (const c of source.concepts) {
        if (targetText.includes(foldTr(c.label))) score += 4;
      }
    }
    if (score > 0) scored.push({ ...(p as WpArticle), _score: score } as RelatedEssay);
  }
  scored.sort((a, b) => (b as RelatedEssay)._score - (a as RelatedEssay)._score);
  const primary = scored.slice(0, limit).map(({ _score: _s, ...rest }) => rest as WpArticle);
  if (primary.length < limit) {
    // Backfill with same-category then newest, so the section is never sparse.
    const seen = new Set(primary.map((p) => p.slug).concat(source.slug));
    for (const p of all) {
      if (primary.length >= limit) break;
      if (seen.has(p.slug)) continue;
      if (p.category === source.category) {
        primary.push(p);
        seen.add(p.slug);
      }
    }
    for (const p of all) {
      if (primary.length >= limit) break;
      if (seen.has(p.slug)) continue;
      primary.push(p);
      seen.add(p.slug);
    }
  }
  return primary;
}

/* ------------------------------------------------------------------ */
/*  Related concepts                                                   */
/* ------------------------------------------------------------------ */

export function findRelatedConcepts(source: KGSourceProfile, limit = 8): GlossaryConcept[] {
  return source.concepts.slice(0, limit);
}

/* ------------------------------------------------------------------ */
/*  Related books                                                      */
/* ------------------------------------------------------------------ */

export function findRelatedBooks(source: KGSourceProfile, limit = 3): KGBook[] {
  const scored = KG_BOOKS.map((b) => {
    const tokens = tokenize([b.title, b.subtitle, b.summary, ...b.keywords].join(" "));
    let score = overlapCount(source.tokens, tokens);
    for (const k of b.keywords) {
      if (source.tokens.has(foldTr(k))) score += 2;
      for (const c of source.concepts) {
        if (foldTr(c.label).includes(foldTr(k)) || foldTr(k).includes(foldTr(c.label))) score += 3;
      }
    }
    return { book: b, score };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  const hits = scored.slice(0, limit).map((x) => x.book);
  // Always surface at least one book so the graph stays populated.
  if (hits.length === 0) return KG_BOOKS.slice(0, Math.min(limit, KG_BOOKS.length));
  return hits;
}

/* ------------------------------------------------------------------ */
/*  Related talks (interviews / conference talks)                       */
/* ------------------------------------------------------------------ */

export type RelatedTalk = Talk;

export function findRelatedTalks(source: KGSourceProfile, limit = 3): RelatedTalk[] {
  const scored = ALL_TALKS.map((t) => {
    const tokens = tokenize([t.topic, t.program].join(" "));
    let score = overlapCount(source.tokens, tokens);
    for (const c of source.concepts) {
      if (foldTr(t.topic).includes(foldTr(c.label))) score += 3;
    }
    return { talk: t, score };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((x) => x.talk);
}

/* ------------------------------------------------------------------ */
/*  Related reports                                                    */
/* ------------------------------------------------------------------ */

export function findRelatedReports(
  source: KGSourceProfile,
  all: ReportRow[],
  limit = 4,
): ReportRow[] {
  const scored = all.map((r) => {
    const tokens = tokenize(
      [r.title, r.organization, r.summary_tr, r.summary_en, ...(r.topics ?? [])].join(" "),
    );
    let score = overlapCount(source.tokens, tokens);
    if (source.category && r.category) {
      const cat = foldTr(source.category);
      if (cat.includes(foldTr(r.category)) || foldTr(r.category).includes(cat)) score += 2;
    }
    for (const c of source.concepts) {
      for (const t of r.topics ?? []) {
        if (foldTr(t).includes(foldTr(c.label)) || foldTr(c.label).includes(foldTr(t))) score += 3;
      }
    }
    return { r, score };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((x) => x.r);
}

/* ------------------------------------------------------------------ */
/*  Related podcast episodes                                           */
/*  (podcast = same essays that have an audioUrl)                      */
/* ------------------------------------------------------------------ */

export function findRelatedPodcasts(
  source: KGSourceProfile,
  all: WpArticle[],
  limit = 3,
): WpArticle[] {
  const withAudio = all.filter((p) => p.hasAudio && p.slug !== source.slug);
  return findRelatedEssays(source, withAudio, limit);
}

/* ------------------------------------------------------------------ */
/*  Composite graph                                                    */
/* ------------------------------------------------------------------ */

export type KnowledgeGraph = {
  essays: WpArticle[];
  concepts: GlossaryConcept[];
  books: KGBook[];
  talks: RelatedTalk[];
  reports: ReportRow[];
  podcasts: WpArticle[];
};

export function computeEssayGraph(
  post: WpArticle,
  lang: KGLang,
  all: WpArticle[],
  reports: ReportRow[],
): KnowledgeGraph {
  const source = buildEssayProfile(post, lang);
  return {
    essays: findRelatedEssays(source, all, 4),
    concepts: findRelatedConcepts(source, 8),
    books: findRelatedBooks(source, 3),
    talks: findRelatedTalks(source, 3),
    reports: findRelatedReports(source, reports, 4),
    podcasts: findRelatedPodcasts(source, all, 3),
  };
}
